import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { Star, GoogleLogo, CircleNotch } from "@phosphor-icons/react";

const Stars = ({ value, size = 16, onSet }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        weight={n <= Math.round(value) ? "fill" : "regular"}
        className={n <= Math.round(value) ? "text-[#F5A623]" : "text-[#CBB994]"}
        onClick={onSet ? () => onSet(n) : undefined}
        style={onSet ? { cursor: "pointer" } : undefined}
        data-testid={onSet ? `review-star-${n}` : undefined}
      />
    ))}
  </div>
);

export default function ReviewsSection() {
  const { t, lang } = useI18n();
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ name: "", rating: 5, comment: "", booking_reference: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.comment.trim()) { toast.error(t("review_fill")); return; }
    if (!form.booking_reference.trim()) { toast.error(t("review_need_booking")); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, booking_reference: form.booking_reference.trim().toUpperCase() });
      toast.success(t("review_thanks"));
      setForm({ name: "", rating: 5, comment: "", booking_reference: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setSubmitting(false); }
  };

  const fmt = (d) => { try { return new Date(d).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

  return (
    <section id="reviews" className="bg-[#F5F2ED] border-y border-[#E9E5DE]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("reviews_kicker")}</p>
        <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-2xl">{t("reviews_title")}</h2>

        <div className="grid lg:grid-cols-3 gap-6 mt-12">
          {/* Google rating badge */}
          <div className="rounded-3xl bg-white border border-[#E9E5DE] p-8 flex flex-col justify-center elev-soft" data-testid="google-rating-card">
            <div className="flex items-center gap-2 mb-4">
              <GoogleLogo size={26} weight="bold" className="text-[#4285F4]" />
              <span className="font-heading font-semibold text-lg text-[#0B1533]">Google</span>
            </div>
            <div className="flex items-end gap-3">
              <span className="font-heading font-bold text-6xl tracking-tight text-[#0B1533]" data-testid="google-rating-value">
                {summary ? summary.average.toFixed(1) : "4.8"}
              </span>
              <div className="pb-2">
                <Stars value={summary ? summary.average : 4.8} size={20} />
                <p className="font-body text-sm text-[#5A607A] mt-1" data-testid="google-review-count">
                  {t("based_on")} {summary ? summary.count.toLocaleString() : "250"} {t("reviews_word")}
                </p>
              </div>
            </div>
            <p className="font-body text-sm text-[#5A607A] mt-6 leading-relaxed">{t("reviews_sub")}</p>
          </div>

          {/* Leave a review */}
          <form onSubmit={submit} className="lg:col-span-2 rounded-3xl bg-white border border-[#E9E5DE] p-8 elev-soft" data-testid="review-form">
            <h3 className="font-heading font-semibold text-xl tracking-tight text-[#0B1533]">{t("leave_review")}</h3>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <input data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("your_name")}
                className="w-full bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#EF5A1E] focus:bg-white focus:outline-none transition-colors duration-200" />
              <div className="flex items-center gap-3 px-1">
                <span className="font-body text-sm text-[#5A607A]">{t("your_rating")}</span>
                <Stars value={form.rating} size={24} onSet={(n) => setForm({ ...form, rating: n })} />
              </div>
            </div>
            <textarea data-testid="review-comment" rows="3" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder={t("your_comment")}
              className="w-full mt-4 bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#EF5A1E] focus:bg-white focus:outline-none transition-colors duration-200" />
            <input data-testid="review-booking-ref" value={form.booking_reference} onChange={(e) => setForm({ ...form, booking_reference: e.target.value.toUpperCase() })} placeholder={t("review_booking_ref")}
              className="w-full mt-4 bg-[#F5F2ED] border border-transparent rounded-2xl px-4 py-3 font-body text-sm focus:border-[#EF5A1E] focus:bg-white focus:outline-none transition-colors duration-200" />
            <button type="submit" disabled={submitting} data-testid="review-submit"
              className="mt-4 inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-7 py-3 hover:bg-[#D2450F] transition-colors duration-200 disabled:opacity-50">
              {submitting && <CircleNotch size={16} className="animate-spin" />}
              {t("submit_review")}
            </button>
          </form>
        </div>

        {/* Review cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {reviews.slice(0, 6).map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-3xl bg-white border border-[#E9E5DE] p-6" data-testid={`review-card-${i}`}>
              <Stars value={r.rating} size={15} />
              <p className="font-body text-sm text-[#3A3F55] mt-3 leading-relaxed">“{r.comment}”</p>
              <div className="flex items-center gap-3 mt-5">
                <div className="w-9 h-9 rounded-full bg-[#0B1533] text-white flex items-center justify-center font-heading font-semibold text-sm">
                  {r.name?.trim()?.[0]?.toUpperCase() || "R"}
                </div>
                <div>
                  <p className="font-body text-sm font-semibold text-[#0B1533]">{r.name}</p>
                  <p className="font-body text-xs text-[#9CA0B0]">{fmt(r.date)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
